import React, { useState, useEffect } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

// Global Settings
import { GLOBAL_DISCOUNT_PERCENT, SALE_NAME, optionLine } from '../config/globalSettings';
import { getImagePath } from '../config/imagePath';

// Import our new dedicated Data file
import { limitedEditionData } from '../data/LimitedEditionData'; 
import '../App.css';

// --- HELPER FUNCTION: EXTRACT MANUFACTURER FROM GUN NAME ---
const getMake = (name) => {
    const n = name.toUpperCase();
    if (n.startsWith("GLOCK")) return "GLOCK";
    if (n.startsWith("SIG")) return "SIG SAUER";
    if (n.startsWith("CZ")) return "CZ";
    if (n.startsWith("SHADOW SYSTEMS")) return "SHADOW SYSTEMS";
    if (n.startsWith("S&W") || n.startsWith("SMITH & WESSON")) return "SMITH & WESSON";
    if (n.startsWith("STACCATO")) return "STACCATO";
    if (n.startsWith("WALTHER")) return "WALTHER"; 
    if (n.startsWith("FN")) return "FN HERSTAL";
    if (n.startsWith("AREX")) return "AREX";
    if (n.startsWith("BERETTA")) return "BERETTA";
    if (n.startsWith("PSA") || n.startsWith("PALMETTO")) return "PALMETTO STATE ARMORY";
    if (n.startsWith("KAHR")) return "KAHR";
    if (n.startsWith("STEYR")) return "STEYR";
    if (n.startsWith("ARCHON")) return "ARCHON";
    if (n.startsWith("HK") || n.startsWith("HECKLER")) return "HECKLER & KOCH";
    if (n.startsWith("ICARUS")) return "ICARUS PRECISION";
    if (n.startsWith("DANIEL")) return "DANIEL DEFENSE";
    if (n.startsWith("IWI")) return "IWI";
    if (n.startsWith("DW ") || n.startsWith("DWX")) return "DAN WESSON";
    if (n.startsWith("1911")) return "1911";
    if (n.startsWith("KIMBER")) return "KIMBER";
    if (n.startsWith("SCCY")) return "SCCY";
    if (n.startsWith("ROST MARTIN")) return "ROST MARTIN";
    return "OTHER";
};

function LimitedEditionBuilder() {
  // --- CORE STATE ---
  const [viewSide, setViewSide] = useState("front"); 
  const [isDaishoUpgrade, setIsDaishoUpgrade] = useState(false);
  const [handId, setHandId] = useState("right");
  const [isLightbearing, setIsLightbearing] = useState(false);
  
  // --- SELECTION STATE ---
  const [manufacturer, setManufacturer] = useState("");
  const [gunId, setGunId] = useState("");
  const [lightId, setLightId] = useState("");
  // First pattern in the data file is the default. This was hard-coded to
  // "frog_skin", which is commented out of colors[] - so on load nothing was
  // selected and selectedColor came back undefined.
  const DEFAULT_COLOR_ID = limitedEditionData.colors[0]?.id || "";
  const [colorId, setColorId] = useState(DEFAULT_COLOR_ID);
  const [wingColorId, setWingColorId] = useState(""); // Daisho only
  // Optional apparel add-on. "" means the customer has not opted in; the shirt
  // only joins the price and the cart once a size is picked.
  const [shirtSizeId, setShirtSizeId] = useState("");
  // The drop ships before the product shoot sometimes. If the photo 404s we drop
  // the thumbnail rather than showing a broken image next to a paid add-on.
  const [shirtImgOk, setShirtImgOk] = useState(true);
  const [mountId, setMountId] = useState("");
  const [clawId, setClawId] = useState("black"); // <-- Defaults to Black

  // --- SHARE FUNCTIONALITY ---
  const {
    getConfigFromURL, generateShareableURL, saveToLocalStorage, loadFromLocalStorage,
    savedConfigs, saveNamedConfig, deleteNamedConfig, copyToClipboard, copySuccess,
    shareModalOpen, setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    handId, isLightbearing, isDaishoUpgrade, gunId, lightId, colorId, wingColorId, mountId, clawId, shirtSizeId
  });

  const loadConfig = (config) => {
    if (!config || config.type === 'click') return;
    setIsDaishoUpgrade(config.isDaishoUpgrade || false);
    setHandId(config.handId || 'right');
    setIsLightbearing(config.isLightbearing || false);
    
    if (config.gunId) {
        const loadedGun = limitedEditionData.guns.find(g => g.id === config.gunId);
        if (loadedGun) setManufacturer(getMake(loadedGun.name));
    }

    setGunId(config.gunId || '');
    setLightId(config.lightId || '');
    setColorId(config.colorId || DEFAULT_COLOR_ID);
    setWingColorId(config.wingColorId || '');
    setShirtSizeId(config.shirtSizeId || '');
    setMountId(config.mountId || '');
    setClawId(config.clawId || 'black');
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (gunId) saveToLocalStorage(getCurrentConfig());
  }, [handId, isLightbearing, isDaishoUpgrade, gunId, lightId, colorId, wingColorId, mountId, clawId, shirtSizeId]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- 1. FILTER: MASTER BASE GUNS ALLOWED FOR MODE ---
  const baseFilteredGuns = limitedEditionData.guns.filter(g => {
      if (handId === 'right' && !isLightbearing && !isDaishoUpgrade) return g.rh_iwb;
      if (handId === 'right' && isLightbearing && !isDaishoUpgrade) return g.rh_lb_iwb;
      if (handId === 'right' && !isLightbearing && isDaishoUpgrade) return g.rh_daisho;
      if (handId === 'right' && isLightbearing && isDaishoUpgrade) return g.rh_lb_daisho;
      
      if (handId === 'left' && !isLightbearing && !isDaishoUpgrade) return g.lh_iwb;
      if (handId === 'left' && isLightbearing && !isDaishoUpgrade) return g.lh_lb_iwb;
      if (handId === 'left' && !isLightbearing && isDaishoUpgrade) return g.lh_daisho;
      if (handId === 'left' && isLightbearing && isDaishoUpgrade) return g.lh_lb_daisho;
      return false;
  });

  // --- 2. FILTER: EXTRACT MANUFACTURERS (ALPHABETICAL) ---
  const availableMakes = Array.from(new Set(baseFilteredGuns.map(g => getMake(g.name))))
      .sort((a, b) => a.localeCompare(b))
      .map(make => ({ id: make, name: make }));

  // --- 3. FILTER: GUNS FOR SELECTED MANUFACTURER (ALPHABETICAL) ---
  const availableGuns = baseFilteredGuns
      .filter(g => getMake(g.name) === manufacturer)
      .sort((a, b) => a.name.localeCompare(b.name));

  // Auto-reset logic if user toggles major settings (Hand/Light/Daisho)
  useEffect(() => {
      if (manufacturer && !availableMakes.find(m => m.id === manufacturer)) {
          setManufacturer("");
          setGunId("");
      } 
      else if (gunId && !availableGuns.find(g => g.id === gunId)) {
          setGunId("");
      }
  }, [baseFilteredGuns, manufacturer, gunId, availableMakes, availableGuns]);

  const selectedGun = limitedEditionData.guns.find(g => g.id === gunId);

  // --- AUTO-RESET LIGHTBEARING TOGGLE ---
  useEffect(() => {
    if (selectedGun) {
      if (selectedGun.supported_lights && selectedGun.supported_lights.length === 0) {
        setIsLightbearing(false); 
        setLightId("");          
      }
    }
  }, [gunId, selectedGun]);

  // --- FILTERING: LIGHTS ---
  const availableLights = isLightbearing && selectedGun
    ? limitedEditionData.lights.filter(light => {
        if (handId === 'left' && selectedGun.supported_lights_lh) {
            return selectedGun.supported_lights_lh.includes(light.id);
        }
        return selectedGun.supported_lights?.includes(light.id);
      })
    : [];

  const selectedLight = limitedEditionData.lights.find(l => l.id === lightId);

  // Auto-reset light if it becomes invalid
  useEffect(() => {
    if (lightId && !availableLights.find(l => l.id === lightId)) {
        setLightId("");
    }
  }, [gunId, handId, availableLights]);


  // Filter Mounts based on Daisho vs IWB
  const availableMounts = limitedEditionData.mounts.filter(m => 
      isDaishoUpgrade ? m.is_daisho : m.is_iwb
  );

  // Auto-reset mount if switching between Daisho/IWB
  useEffect(() => {
      if (mountId && !availableMounts.find(m => m.id === mountId)) {
          setMountId("");
      }
  }, [isDaishoUpgrade]);


  // --- DYNAMIC CLAW LOGIC ---
  const displayClaws = limitedEditionData.claws
    .filter(claw => isDaishoUpgrade ? claw.id !== "no" : true)
    .map(claw => ({
        ...claw,
        price: isDaishoUpgrade ? 0 : claw.price
    }));

  // Auto-set to Black if they turn Daisho on and "No Thanks" was selected
  useEffect(() => {
      if (isDaishoUpgrade && clawId === "no") {
          setClawId("black");
      }
  }, [isDaishoUpgrade, clawId]);


  // --- SELECTIONS ---
  const selectedColor = limitedEditionData.colors.find(c => c.id === colorId);
  const apparel = limitedEditionData.apparel || null;
  const selectedShirtSize = apparel?.sizes.find(sz => sz.id === shirtSizeId) || null;
  const selectedWingColor = limitedEditionData.wing_colors.find(c => c.id === wingColorId);
  const selectedMount = limitedEditionData.mounts.find(m => m.id === mountId);
  const selectedClaw = limitedEditionData.claws.find(c => c.id === clawId);

  // --- LEAD TIME LOGIC (FIXED 10 DAYS) ---
  const calculateLeadTime = () => {
    if (!selectedGun) return { text: "Select Options", date: "...", type: "PENDING" };
    
    const targetDays = 10; 
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    return { 
        type: "LIMITED EDITION DROP", 
        timeline: "10 Business Days", 
        date: dateString, 
        color: "#D5A678", 
        isCustom: false 
    };
  };
  const leadTimeInfo = calculateLeadTime();

  // --- PRICING ---
  const BASE_PRICE = 96.00; 
  const DAISHO_UPGRADE_PRICE = 70.00; 
  
  const subtotal = 
    BASE_PRICE +
    (isDaishoUpgrade ? DAISHO_UPGRADE_PRICE : 0) +
    (selectedColor?.price || 0) +
    (selectedMount?.price || 0) +
    (isDaishoUpgrade && selectedWingColor?.price ? selectedWingColor.price : 0) +
    (!isDaishoUpgrade && selectedClaw?.price ? selectedClaw.price : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;

  // The shirt is a separate cart line at its own fixed builder price, so it sits
  // outside the holster discount. holsterTotal is what goes to anr_price; total
  // is only what the customer sees as the estimated basket.
  const holsterTotal = (subtotal - discountAmount).toFixed(2);
  const shirtPrice = selectedShirtSize ? (apparel?.builder_price || 0) : 0;
  const total = (parseFloat(holsterTotal) + shirtPrice).toFixed(2);

  // Validation
  let isReady = gunId && colorId && mountId && clawId;
  if (isLightbearing && !lightId) isReady = false;
  if (isDaishoUpgrade && !wingColorId) isReady = false;

  // --- ADD TO CART ---
  const handleAddToCart = () => {
    if (!isReady) return;

    const configuration = [
        `Base Style: ${isDaishoUpgrade ? "Daisho System Upgrade" : "Standard IWB"}`,
        `Gun Model: ${selectedGun?.name}`,
        isLightbearing ? `Light Model: ${selectedLight?.name}` : `Light Model: None`,
        `Hand: ${handId === 'right' ? 'Right' : 'Left'}`,
        `Color: ${selectedColor?.name}`,
        isDaishoUpgrade ? `Daisho Wing Color: ${selectedWingColor?.name}` : null,
        `Mount: ${selectedMount?.name}`,
        optionLine("Claw Option", selectedClaw?.name),
        `Warranty: Free Lifetime Warranty`,
        selectedShirtSize ? `Add-On: ${apparel.name} (${selectedShirtSize.name})` : null,
        `Lead Time: ${leadTimeInfo.timeline}`,
    ].filter(Boolean).join('\n');

    // 283913 is the PARENT "Custom Limited Edition Builder" product. Each pattern
    // is a variation of it (Chocolate Chip = 283914) and carries its own stock.
    // This was previously hard-coded to 283914 - the Chocolate Chip VARIATION id -
    // which resolved correctly while Chocolate Chip was the only pattern, but would
    // have booked every other pattern's sale against Chocolate Chip's stock.
    const productID = 283913;
    const variationID = selectedColor?.woo_variation_id || ''; 
    
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    
    if (variationID) {
        params.append('variation_id', variationID); 
        // Attribute slug is "patterns" (plural) - see the variation permalink:
        // /product/custom-limited-edition-builder/?attribute_patterns=Chocolate+Chip
        params.append('attribute_patterns', selectedColor.name); 
    }
    
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', holsterTotal);

    // Optional second line: the apparel add-on. The plugin picks these up after
    // WooCommerce has handled the holster and adds the shirt as its own item at
    // the builder price. Skipped entirely when no size was chosen.
    if (selectedShirtSize && apparel?.woo_product_id) {
        params.append('anr_addon_id', apparel.woo_product_id);
        params.append('anr_addon_variation', selectedShirtSize.woo_variation_id);
        params.append('anr_addon_price', apparel.builder_price);
        params.append(`anr_addon_attr_${apparel.attribute_slug}`, selectedShirtSize.name);
    }

    window.location.href = `/checkout/?${params.toString()}`;
  };

  const SectionHeader = ({ title, description, imageUrl }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
        <h3 style={{ textTransform: "uppercase", margin: 0, fontSize: "14px", color: "#D5A678" }}>{title}</h3>
        {(description || imageUrl) && (
            <div className="tooltip-wrapper">
                <span className="info-icon">i</span>
                <div className="tooltip-content">
                    {imageUrl && (
                        <div style={{marginBottom: "10px"}}>
                            <img src={imageUrl} alt="Info" style={{width: "100%", borderRadius: "4px"}} />
                        </div>
                    )}
                    {description}
                </div>
            </div>
        )}
    </div>
  );

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">Limited Edition IWB Builder <br/><span style={{fontSize:"16px", color:"#D5A678"}}>EXCLUSIVE PATTERN DROP</span></h1>
        
        <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.7", marginBottom: "40px" }}>
            <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase", marginBottom: "15px" }}>Features</h3>
            <ul style={{ paddingLeft: "20px", marginBottom: "20px", listStyleType: "disc" }}>
                <li><strong style={{color: "#fff"}}>KYDEX® Thermoplastics Construction:</strong> Durable, lightweight, and sweat-resistant.</li>
                <li><strong style={{color: "#fff"}}>IWB Design:</strong> Comfortable and discreet carry.</li>
                <li><strong style={{color: "#fff"}}>Full Trigger Guard Protection:</strong> Ensures a safe and secure carry.</li>
                <li><strong style={{color: "#fff"}}>Positive Retention:</strong> Retention hardware included so you can set draw tension to your preference.</li>
                <li><strong style={{color: "#fff"}}>Optional Concealment Claw:</strong> Superior concealment.</li>
                <li><strong style={{color: "#fff"}}>Precision Fit:</strong> CNC cut and vacuum formed for a precise, secure fit with your firearm.</li>
                <li><strong style={{color: "#fff"}}>Made in the USA</strong></li>
            </ul>
            <p style={{ marginTop: "15px", fontWeight: "bold", color: "#D5A678", textTransform: "uppercase" }}>
                ALL HOLSTERS ARE OPTIC READY AND COMPATIBLE WITH A THREADED BARREL AND SUPPRESSOR-HEIGHT SIGHTS UP TO 0.450″.
            </p>
        </div>

        {/* 1. DAISHO MASTER SWITCH */}
        <div style={{ 
            backgroundColor: "#1e1e1e", 
            borderLeft: "2px solid #D5A678", 
            padding: "15px", 
            marginBottom: "30px" 
        }}>
            <h3 style={{ textTransform: "uppercase", margin: "0 0 10px 0" }}>Upgrade to Daishō System?</h3>
            <div style={{ fontSize: "13px", color: "#ccc", marginBottom: "20px", lineHeight: "1.6" }}>
                <strong style={{color: "#fff"}}>Optional Upgrade:</strong> Carry your pistol and a spare magazine together with the Daishō, ANR Design's sidecar holster system. Pairing a precision-formed KYDEX® Thermoplastics holster with a magazine carrier on a Curv composite bridge, the Daishō streamlines your everyday carry setup while maximizing accessibility and comfort.
                <ul style={{ paddingLeft: "20px", marginTop: "10px", listStyleType: "disc" }}>
                    <li><strong style={{color: "#fff"}}>Seamless Integration:</strong> A Curv composite bridge securely connects your holster and magazine carrier, minimizing bulk and enhancing concealment.</li>
                    <li><strong style={{color: "#fff"}}>Fixed Geometry:</strong> Wing and magazine carrier positions are set during manufacture for a consistent, repeatable draw.</li>
                    <li><strong style={{color: "#fff"}}>Ready to Use:</strong> The Daishō arrives fully assembled, so you can hit the range or carry with confidence right away.</li>
                </ul>
            </div>
            <SegmentedControl 
                label="" 
                options={[
                    {id: false, name: 'Standard IWB'}, 
                    {id: true, name: `Daisho Upgrade (+ $${DAISHO_UPGRADE_PRICE.toFixed(2)})`}
                ]} 
                selectedValue={isDaishoUpgrade} 
                onChange={setIsDaishoUpgrade} 
            />
        </div>

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* 2. CORE CONFIGURATION FILTERS */}
        <SegmentedControl label="Draw Hand" options={[{id: 'right', name: 'Right'}, {id: 'left', name: 'Left'}]} selectedValue={handId} onChange={setHandId} />
        <div style={{marginTop:"20px"}}></div>
        <SegmentedControl 
            label="Add Weapon Light?" 
            options={[{id: true, name: 'Yes'}, {id: false, name: 'No'}]} 
            selectedValue={isLightbearing} 
            onChange={(val) => {
                setIsLightbearing(val);
                setLightId(""); 
            }} 
        />

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* 3. MANUFACTURER -> FIREARM -> LIGHT CASCADE */}
        <OptionSelector 
            label="Select Manufacturer *" 
            options={availableMakes} 
            selectedValue={manufacturer} 
            onChange={(val) => {
                setManufacturer(val);
                setGunId(""); 
            }} 
        />

        <OptionSelector 
            label="Select Firearm Model *" 
            options={availableGuns} 
            selectedValue={gunId} 
            onChange={setGunId} 
            disabled={!manufacturer}
        />
        
        {isLightbearing && (
            <OptionSelector 
                label="Select Weapon Light *" 
                options={availableLights} 
                selectedValue={lightId} 
                onChange={setLightId} 
                disabled={!gunId}
            />
        )}

        {/* HELPER MESSAGE FOR NO LIGHTS */}
        {selectedGun && selectedGun.supported_lights && selectedGun.supported_lights.length === 0 && (
          <div style={{ marginTop: "15px", marginBottom: "20px", padding: "15px", backgroundColor: "#1e1e1e", border: "1px solid #444", borderRadius: "4px", fontSize: "14px", color: "#ccc" }}>
              <strong style={{color: "#D5A678", display: "block", marginBottom: "5px"}}>No Lightbearing Models Available</strong>
              <p style={{margin: "0 0 10px 0", fontSize: "13px"}}>We do not currently offer a standard lightbearing option for this specific firearm model.</p>
              <a href="https://www.anrkydexholsters.com/custom-holster-request/" target="_blank" rel="noopener noreferrer" style={{color: "#D5A678", textDecoration: "underline", fontSize: "13px", fontWeight: "bold"}}>Custom Holster Request | ANR Design Holsters</a>
          </div>
        )}

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        <SectionHeader title="Limited Pattern" description="Exclusive limited run pattern. Available while supplies last." />
        
        {/* CUSTOM INLINE PATTERN SELECTOR */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", marginTop: "15px", marginBottom: "20px" }}>
            {limitedEditionData.colors.map((color) => (
                <div 
                    key={color.id}
                    onClick={() => setColorId(color.id)}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        cursor: "pointer",
                        width: "80px",
                    }}
                >
                    <div style={{
                        width: "65px",
                        height: "65px",
                        borderRadius: "50%",
                        border: colorId === color.id ? "3px solid #D5A678" : "3px solid #333",
                        boxShadow: colorId === color.id ? "0 0 15px rgba(213, 166, 120, 0.4)" : "none",
                        backgroundImage: `url(${getImagePath(`swatches/${color.id}.jpg`)})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        marginBottom: "10px",
                        transition: "all 0.2s ease"
                    }} title={color.name} />
                    <span style={{ 
                        fontSize: "12px", 
                        textAlign: "center", 
                        color: colorId === color.id ? "#D5A678" : "#888",
                        fontWeight: colorId === color.id ? "bold" : "normal",
                        lineHeight: "1.2"
                    }}>
                        {color.name}
                    </span>
                </div>
            ))}
        </div>

        {/* SELECTED PATTERN DESCRIPTION */}
        {selectedColor?.description && (
            <div style={{
                marginTop: "-5px",
                marginBottom: "25px",
                padding: "15px",
                backgroundColor: "rgba(213, 166, 120, 0.1)",
                borderLeft: "3px solid #D5A678",
                borderRadius: "4px",
                fontSize: "13px",
                lineHeight: "1.6",
                color: "#ccc"
            }}>
                <strong style={{
                    color: "#D5A678",
                    display: "block",
                    marginBottom: "6px",
                    textTransform: "uppercase",
                    fontSize: "12px",
                    letterSpacing: "0.5px"
                }}>
                    {selectedColor.name}
                </strong>
                {selectedColor.description}
            </div>
        )}
        
        {isDaishoUpgrade && (
            <div style={{ marginTop: "30px" }}>
                <SectionHeader 
                    title="Daishō Wing Color" 
                    description="Select the color for the Curv composite bridge wing. The wing carries your magazine carrier alongside the holster in a fixed, factory-set position." 
                />
                <OptionSelector 
                    label="Choose Wing Color *" 
                    options={limitedEditionData.wing_colors} 
                    selectedValue={wingColorId} 
                    onChange={setWingColorId} 
                />
            </div>
        )}

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* MOUNTING */}
        <SectionHeader title="Mounting Options" />
        <OptionSelector label="Select Mount *" options={availableMounts} selectedValue={mountId} onChange={setMountId} />

        {/* CLAW COLOR SELECTION */}
        <div style={{marginTop: "30px"}}></div>
        <SectionHeader title="Concealment Claw" description="Select the color for the concealment claw attached to the holster shell." />
        <OptionSelector 
            label="Select Claw Color*" 
            options={displayClaws} 
            selectedValue={clawId} 
            onChange={setClawId} 
        />

        {/* --- APPAREL ADD-ON (optional) --- */}
        {apparel && (
          <div style={{marginTop: "30px"}}>
            <SectionHeader title={apparel.name} description={apparel.description} />

            <div className="apparel-row">
                {apparel.img && shirtImgOk && (
                    <div className="tooltip-wrapper apparel-thumb-wrap">
                        <img
                            className="apparel-thumb"
                            src={apparel.img}
                            alt={apparel.name}
                            onError={() => setShirtImgOk(false)}
                        />
                        {/* Enlarged on hover; the mobile rules turn this into a
                            centred tap-to-view modal automatically. */}
                        <div className="tooltip-content">
                            <img src={apparel.img} alt={apparel.name} />
                        </div>
                    </div>
                )}

                <div className="apparel-controls">
                    <OptionSelector
                        label="Add a Shirt (optional)"
                        options={apparel.sizes.map(sz => ({
                            id: sz.id,
                            name: sz.name,
                            price: apparel.builder_price
                        }))}
                        selectedValue={shirtSizeId}
                        onChange={setShirtSizeId}
                    />
                    <div className="apparel-price-row">
                        <span>
                            ${apparel.builder_price.toFixed(2)} with a holster
                            <span className="apparel-was">${apparel.retail_price.toFixed(2)}</span>
                        </span>
                        {selectedShirtSize && (
                            <button
                                type="button"
                                className="apparel-remove"
                                onClick={() => setShirtSizeId("")}
                            >
                                REMOVE
                            </button>
                        )}
                    </div>
                </div>
            </div>
          </div>
        )}

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        {/* COLLAPSIBLE DESCRIPTION */}
        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                Experience the ultimate in concealed carry convenience with our Limited Edition line. 
                Whether you choose the ultra-discreet standard IWB or upgrade to the <strong>Daishō</strong> sidecar system, 
                you are getting a precision-formed, American-made rig.
            </p>
            <p style={{ color: "#D5A678", fontWeight: "bold", marginBottom: 0 }}>
                ALL HOLSTERS ARE OPTIC READY AND COMPATIBLE WITH A THREADED BARREL AND SUPPRESSOR-HEIGHT SIGHTS UP TO 0.450″.
            </p>
        </CollapsibleSection>

        {/* COLLAPSIBLE WARNINGS */}
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={gunId} />
            </CollapsibleSection>
        </div>
      </div>

      <div className="preview-section">
        <div className="sticky-wrapper">

          <HolsterVisualizer 
            holsterType={isDaishoUpgrade ? "daisho" : "iwb"} 
            gunData={selectedGun} 
            lightData={isLightbearing ? selectedLight : null}
            colorData={selectedColor} 
            wingData={isDaishoUpgrade ? selectedWingColor : null}
            mountData={selectedMount} 
            clawData={selectedClaw} 
            isLightbearing={isLightbearing ? "yes" : "no"} 
            isLeftHanded={handId === 'left'}
            viewSide={viewSide} 
            hideDefaultScrews={true} 
          />

          {/* --- FRONT / REAR TOGGLE --- */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {leadTimeInfo.type !== "PENDING" && (
            <div className="lead-time-box" style={{ 
                backgroundColor: "rgba(213, 166, 120, 0.1)", 
                padding: "15px", 
                borderLeft: `4px solid ${leadTimeInfo.color}`, 
                marginBottom: "20px"
            }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                        {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "15px" }}>{leadTimeInfo.type}</strong>}
                        <div style={{ marginTop: "5px", color: "#fff", fontSize: "13px", lineHeight: "1.5" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                        </div>
                    </div>
                </div>
            </div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>
                    ${subtotal.toFixed(2)}
                </span>
                <span style={{ color: "#00ff00" }}>
                    ${total}
                </span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", letterSpacing: "1px", marginTop: "5px" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={gunId} />
          
          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
            {isReady ? "ADD TO CART" : "SELECT OPTIONS"}
          </button>
        </div>
      </div>
      
      {/* MOBILE BAR */}
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container">
          <span className="mobile-label">Estimated Total</span>
          <span className="mobile-total">${total}</span>
        </div>
        <button className={`mobile-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
            {isReady ? "ADD TO CART" : "INCOMPLETE"}
        </button>
      </div>

      {/* SHARE MODAL */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareableURL={shareableURL}
        currentConfig={getCurrentConfig()}
        savedConfigs={savedConfigs}
        onSaveConfig={saveNamedConfig}
        loadConfig={loadConfig}
        onDeleteConfig={deleteNamedConfig}
        onCopyLink={copyToClipboard}
        copySuccess={copySuccess}
        gunName={selectedGun?.name}
        colorName={selectedColor?.name}
      />
    </div>
  );
}

export default LimitedEditionBuilder;